import { useEffect } from 'react';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { getUnsavedChangesFunctionsForArea, UnsavedChangesArea } from '@hooks-clean/unsaved-changes/shared';

export type UseUnsavedChangesTrackingInnerProps = {
    componentName: string;
    hasChanges: boolean;
    setChangesFn: (key: string, value: boolean) => void;
};

/**
 * **Do not use this - use `useUnsavedChangesTracking` instead**
 *
 * **This exists for unit-testing purposes**
 */
export function useUnsavedChangesTrackingInner({
    componentName,
    hasChanges,
    setChangesFn,
}: UseUnsavedChangesTrackingInnerProps) {
    useEffect(() => {
        setChangesFn(componentName, hasChanges);

        // This clears the changes when...
        // - component unmounts
        // - the setter changes (removes change from the old setter, the body will then use the new setter)
        // - `hasChanges` changes, but the body of the effect will then set it back
        return () => {
            setChangesFn(componentName, false);
        };
    }, [setChangesFn, componentName, hasChanges]);
}

export type UseUnsavedChangesTrackingProps = {
    area: UnsavedChangesArea;
    componentName: string;
    hasChanges: boolean;
};

/**
 * Sets up unsaved changes tracking for a component.
 *
 * When `hasChanges` changes, the appropriate (based on `area`) unsaved-changes tracker is updated.
 *
 * When hook unmounts, the change is automatically removed from the unsaved change tracker.
 */
export function useUnsavedChangesTracking({ area, componentName, hasChanges }: UseUnsavedChangesTrackingProps) {
    const context = useUnsavedChangesContext();

    // Get the setter function for the right unsaved changes map based on `type`
    const { setChanges } = getUnsavedChangesFunctionsForArea(context, area);

    return useUnsavedChangesTrackingInner({
        componentName,
        hasChanges,
        setChangesFn: setChanges,
    });
}
